import { custom_headers } from '../../../../domain/headers';
import { BillingAlert, BillingAlertProduct } from '../../../../typings/BillingAlert.interface';
import {
  FilterCompletionInput,
  FilterCompletionOutput,
  TransformationCompletionInput,
  TransformationCompletionOutput,
} from '../../../../typings/AICompletion.interface';
import { APIAccountSessionList } from '../../../../typings/AccountSession.interface';
import { APIList } from '../../../../typings/API.interface';
import { ApiKey } from '../../../../typings/ApiKey.interface';
import {
  APIBatchOperation,
  BatchOperationPlan,
} from '../../../../typings/BatchOperation.interface';
import { Bookmark } from '../../../../typings/Bookmark.interface';
import {
  APIAccountCliKey,
  APICreatedCliToken,
  CliClient,
  CliKeyAccessInput,
} from '../../../../typings/CliClient.interface';
import { CliSession } from '../../../../typings/CliSession.interface';
import { CFCustomDomain, CustomDomainAddDeleteResult } from '../../../../typings/CustomDomain';
import { APIDestination } from '../../../../typings/Destination.interface';
import { Event, EventWithData } from '../../../../typings/Event.interface';
import { EventAttempt } from '../../../../typings/EventAttempt.interface';
import { IgnoredEvent } from '../../../../typings/IgnoredEvent.interface';
import {
  DestinationType,
  DestinationTypeSchema,
  SourceType,
  SourceTypeSchema,
} from '../../../../typings/Integration.interface';
import { Issue, IssueWithData } from '../../../../typings/Issue.interface';
import {
  APIIssueTrigger,
  APIIssueTriggerChannels,
} from '../../../../typings/IssueTrigger.interface';
import {
  APIOrganization,
  APIOrganizationWithRoleAndPlan,
  OrganizationSecuritySettings,
  OrganizationSecuritySettingsUpdateInput,
  OrganizationUpdateInput,
} from '../../../../typings/Organization.interface';
import {
  OrganizationMember,
  OrganizationMemberInvite,
  OrganizationRole,
} from '../../../../typings/OrganizationMember.interface';
import {
  OutpostAttempt,
  OutpostAttemptListParams,
  OutpostDestination,
  OutpostDestinationCreateInput,
  OutpostDestinationListParams,
  OutpostDestinationTypeMetadata,
  OutpostDestinationUpdateInput,
  OutpostEvent,
  OutpostEventListParams,
  OutpostEventListResponse,
  OutpostPublishEventInput,
  OutpostRetryResponse,
  OutpostTenant,
  OutpostTenantPortalResponse,
  OutpostTenantTokenResponse,
  OutpostTenantUpsertInput,
} from '../../../../typings/outpost';
import {
  OperatorConfig,
  OperatorConfigUpdateInput,
  OutpostStaticIpFleet,
  OutpostStatusResponse,
} from '../../../../typings/OutpostDeployment.interface';
import { Request } from '../../../../typings/Request.interface';
import { SigningSecret } from '../../../../typings/SigningSecret.interface';
import { APISource } from '../../../../typings/Source.interface';
import { APISubscription } from '../../../../typings/Subscription.interface';
import {
  APITeam,
  APITeamWithRole,
  ProjectTypeInput,
  TeamUpdateInput,
} from '../../../../typings/Team.interface';
import { TeamIntegration } from '../../../../typings/TeamIntegration.interface';
import { TeamMember, TeamRole } from '../../../../typings/TeamMember.interface';
import {
  Transformation,
  TransformationExecutorOutput,
  TransformationRunInput,
} from '../../../../typings/Transformation.interface';
import { TransformationExecution } from '../../../../typings/TransformationExecution.interface';
import { User } from '../../../../typings/User.interface';
import { View, ViewCreateInput, ViewUpdateInput } from '../../../../typings/View.interface';
import { APIWebhook, WebhookFilterProperty } from '../../../../typings/Webhook.interface';
import {
  Authenticator,
  BackupCodesCountResponse,
  BackupCodesResponse,
  LoginResponse,
  LoginSuccessResponse,
  MFAComplianceStatus,
  MultiFactorSetupResponse,
  MultiFactorStatusResponse,
  MultiFactorVerifyAuthenticatorResponse,
} from '../typings/MultiFactor.interface';
import {
  BillingAddress,
  OrbPlan,
  OrbSubscription,
  StandardizedInvoice,
  TaxID,
} from '../typings/Orb.interface';
import { stringifyQuery } from '../utils';

type HookdeckAPIErrorResponse = {
  message: string;
  status: number;
  data: any;
  code: string;
};

/**
 * Addon key type for generic addon API.
 * Matches the backend AddonKey type.
 */
export type AddonKey = 'static_ip';

/**
 * Information about an addon with pricing.
 * Returned by the getAddons() API endpoint.
 */
export interface AddonInfo {
  key: AddonKey;
  display_name: string;
  description: string;
  is_per_project: boolean;
  price: {
    price_id: string;
    unit_amount: number;
    currency: string;
  } | null;
  enabled_count: number;
}

export class HookdeckAPIError extends Error {
  response: string | HookdeckAPIErrorResponse | null;
  status: number;

  constructor(status: number, response: string | HookdeckAPIErrorResponse | null) {
    super('Hookdeck API Error');
    this.response = response;
    this.status = status;
  }
}

export const isUnauthorizedError = (error: unknown): boolean =>
  error instanceof HookdeckAPIError && (error.status === 401 || error.status === 403);

/**
 * The MFA setup page to send the user to when the API refused the request because the
 * organization acted on requires MFA (403 mfa_setup_required), coming back to the current page
 * afterwards. Null for any other error.
 */
export const getMFASetupUrl = (error: unknown): string | null => {
  if (!(error instanceof HookdeckAPIError) || error.status !== 403) {
    return null;
  }
  const response = error.response as { error?: string; redirect?: string } | string | null;
  if (
    typeof response !== 'object' ||
    response?.error !== 'mfa_setup_required' ||
    !response.redirect
  ) {
    return null;
  }

  const setup_url = new URL(response.redirect, window.location.origin);
  setup_url.searchParams.set('redirect', window.location.pathname + window.location.search);
  return setup_url.toString();
};

type Method = 'POST' | 'GET' | 'PUT' | 'DELETE' | 'PATCH';

export const DEFAULT_API_VERSION = '2026-09-01';

export type RawBodyResponse = { body: string } | Blob;

export default class HookdeckAPI {
  api_url: string;
  outpost_api_url: string;
  team_id: string;
  local_cache: { [key: string]: { result: any; duration: number } };

  constructor(api_url: string, team_id: string, outpost_api_url?: string) {
    this.api_url = api_url;
    this.outpost_api_url = outpost_api_url || api_url;
    this.team_id = team_id;
    this.local_cache = {};
  }

  request<T>(method: Method, path: string, data?: object, signal?: AbortSignal): Promise<T> {
    return this.makeRequest<T>(this.api_url, method, path, data, signal) as Promise<T>;
  }

  outpostRequest<T>(
    method: Method,
    path: string,
    data?: object,
    signal?: AbortSignal,
    null_on_status_codes: number[] = [],
  ): Promise<T> {
    return this.makeRequest<T>(
      this.outpost_api_url,
      method,
      path,
      data,
      signal,
      null_on_status_codes,
    ) as Promise<T>;
  }

  private makeRequest<T>(
    base_url: string,
    method: Method,
    path: string,
    data?: object,
    signal?: AbortSignal,
    null_on_status_codes: number[] = [],
    override_team_id?: string,
  ): Promise<T | null> {
    const effective_team_id = override_team_id ?? this.team_id;
    const team_id_header = effective_team_id
      ? { [custom_headers.x_team_id]: effective_team_id }
      : {};

    let url = `${base_url}/${DEFAULT_API_VERSION}${path}`;
    if (method === 'GET' && data) {
      url += `?${stringifyQuery(data)}`;
    }
    return fetch(url, {
      method,
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        ...team_id_header,
      },
      credentials: 'include',
      ...(method !== 'GET' && data ? { body: JSON.stringify(data) } : {}),
      ...(signal ? { signal } : {}),
    }).then(async (response) => {
      const content_type = response.headers
        .get('content-type')
        ?.split(';', 1)[0]
        .trim()
        .toLowerCase();
      const is_json = content_type === 'application/json' || content_type?.endsWith('+json');

      if (response.ok && response.status !== 204 && content_type && !is_json) {
        return (await response.blob()) as T;
      }

      // Read as text so a failed body read (connection dropped mid-stream, abort) rejects
      // instead of resolving null, and an empty body (204s) still resolves null.
      const body_text = await response.text();
      let response_body: string | HookdeckAPIErrorResponse | null = null;
      if (body_text) {
        try {
          response_body = JSON.parse(body_text);
        } catch {
          if (response.ok) {
            throw new HookdeckAPIError(response.status, body_text);
          }
          response_body = body_text;
        }
      }
      if (response.ok) {
        return response_body as T;
      }
      if (null_on_status_codes.includes(response.status)) {
        return null;
      }
      throw new HookdeckAPIError(response.status, response_body);
    });
  }

  async requestWithCache<T>(
    method: Method,
    url: string,
    data: object = {},
    duration = 1000 * 5,
  ): Promise<T> {
    const key = `${method}-${url}-${JSON.stringify(data)}`;
    if (this.local_cache[key] && this.local_cache[key].duration > Date.now()) {
      return this.local_cache[key].result as T;
    }
    const result = await this.request(method, url, data);
    this.local_cache[key] = { result, duration: Date.now() + duration };
    return result as T;
  }

  billing = {
    getAlert: (product: BillingAlertProduct) =>
      this.request<BillingAlert>('GET', `/internal/billing/alerts/${product}`),
    setAlert: (product: BillingAlertProduct, threshold: number | null) =>
      this.request<BillingAlert>('PUT', `/internal/billing/alerts/${product}`, { threshold }),
    getThroughputTiers: () =>
      this.request<{
        [key: string]: {
          price: number;
          compare_at_price: number;
          legacy: boolean;
        };
      }>('GET', '/internal/billing/throughput/tiers'),
    startTeamMemberTrial: () => this.request<APISubscription>('POST', '/internal/billing/trial'),
    updateThroughput: (tier: number) =>
      this.request<APITeam>('POST', '/internal/billing/throughput/update', { tier }),
    getToken: () => this.request<{ client_secret: string }>('GET', '/internal/billing/token'),
    getSubscriptionDetails: () =>
      this.request<OrbSubscription>('GET', '/internal/billing/subscription/details'),
    getOutpostSubscriptionDetails: () =>
      this.request<OrbSubscription>('GET', '/internal/billing/outpost/subscription/details'),
    getOutpostStaticIPs: () =>
      this.request<OutpostStaticIpFleet[]>('GET', '/internal/billing/outpost/static_ips'),
    selectPlan: (plan: string) =>
      this.request<APISubscription | { sessionId: string }>('POST', '/internal/billing/checkout', {
        plan,
      }),
    getUsage: (filters?: object) =>
      this.request<{
        data: {
          usage: {
            quantity: number;
            timeframe_start: string;
            timeframe_end: string;
          }[];
          billable_metric: { id: string; name: string };
          view_mode: 'periodic' | 'cumulative';
        }[];
      }>('GET', '/internal/billing/usage', filters),
    getPlans: (filters?: object) =>
      this.request<OrbPlan[]>('GET', '/internal/billing/plans', filters),
    getOutpostPlans: (filters?: object) =>
      this.request<OrbPlan[]>('GET', '/internal/billing/outpost/plans', filters),
    getInvoices: () => this.request<StandardizedInvoice[]>('GET', '/internal/billing/invoices'),
    retryInvoicePayment: (invoice_id: string) =>
      this.request<StandardizedInvoice>('POST', `/internal/billing/invoices/${invoice_id}/pay`),
    getCard: () =>
      this.request<{
        brand: string;
        exp_month: number;
        exp_year: number;
        last4: string;
      }>('GET', '/internal/billing/card'),
    changePlan: (plan_name: string) =>
      this.request<APISubscription>('POST', '/internal/billing/plan', { plan_name }),
    changeOutpostPlan: (plan_name: string) =>
      this.request<APISubscription>('POST', '/internal/billing/outpost/plan', { plan_name }),
    updateCard: () =>
      this.request<{
        sessionId: string;
      }>('POST', '/internal/billing/card'),
    cancel: () => this.request<APISubscription>('PUT', '/internal/billing/cancel'),
    cancelOutpost: () => this.request<APISubscription>('PUT', '/internal/billing/outpost/cancel'),
    getBillingEmail: () => this.request<{ email: string }>('GET', '/internal/billing/email'),
    updateBillingEmail: (email) =>
      this.request<{ email: string }>('PUT', '/internal/billing/email', { email }),
    setPaymentMethodAsDefault: (payment_method_id: string) =>
      this.request<{ email: string }>('PUT', '/internal/billing/default_payment_method', {
        payment_method_id: payment_method_id,
      }),
    getBillingAddress: () =>
      this.request<{ billing_address: BillingAddress | null; tax_id: TaxID | null }>(
        'GET',
        '/internal/billing/address',
      ),
    updateBillingAddress: (billing_address: BillingAddress, tax_id: TaxID | null) =>
      this.request<{ billing_address: BillingAddress; tax_id: TaxID | null }>(
        'PUT',
        '/internal/billing/address',
        {
          billing_address,
          tax_id,
        },
      ),
    // Generic addon methods
    getAddons: () => this.request<AddonInfo[]>('GET', '/internal/billing/addons'),
    getAddonPrice: (addon_key: AddonKey) =>
      this.request<{ price_id: string; unit_amount: number; currency: string } | null>(
        'GET',
        `/internal/billing/addons/${addon_key}/price`,
      ),
    /**
     * Enable an addon for the current team.
     * @param addon_key - The addon to enable
     * @param confirmed_price - Optional price in cents to validate against server price.
     *                          Prevents billing discrepancies from race conditions.
     */
    enableAddon: (addon_key: AddonKey, confirmed_price?: number) =>
      this.request<APITeam>('POST', `/internal/billing/addons/${addon_key}/enable`, {
        ...(confirmed_price !== undefined && { confirmed_price }),
      }),
    /**
     * Enable an addon for multiple teams.
     * @param addon_key - The addon to enable
     * @param team_ids - The team IDs to enable the addon for
     * @param confirmed_price - Optional price in cents to validate against server price.
     *                          Prevents billing discrepancies from race conditions.
     */
    enableAddonBulk: (addon_key: AddonKey, team_ids: string[], confirmed_price?: number) =>
      this.request<{ updated_teams: APITeam[] }>(
        'POST',
        `/internal/billing/addons/${addon_key}/enable-bulk`,
        { team_ids, ...(confirmed_price !== undefined && { confirmed_price }) },
      ),
    disableAddon: (addon_key: AddonKey) =>
      this.request<APITeam>('POST', `/internal/billing/addons/${addon_key}/disable`),
    disableAddonBulk: (addon_key: AddonKey, team_ids: string[]) =>
      this.request<{ updated_teams: APITeam[] }>(
        'POST',
        `/internal/billing/addons/${addon_key}/disable-bulk`,
        { team_ids },
      ),
    disableAllAddon: (addon_key: AddonKey) =>
      this.request<{ affected_teams: APITeam[] }>(
        'POST',
        `/internal/billing/addons/${addon_key}/disable-all`,
      ),
  };

  users = {
    delete: () => this.request<{ id: string }>('DELETE', `/session/me`),
  };

  completion = {
    completeFilter: (input: FilterCompletionInput) =>
      this.request<FilterCompletionOutput>('POST', '/internal/completions/filter', input),
    completeTransformation: (input: TransformationCompletionInput) =>
      this.request<TransformationCompletionOutput>(
        'POST',
        '/internal/completions/transformation',
        input,
      ),
  };

  console = {
    getUserOrCreateGuest: (params?: {
      source_id?: string;
      utm_source?: string;
      utm_medium?: string;
      utm_campaign?: string;
      utm_content?: string;
      utm_term?: string;
      referrer?: string;
    }) =>
      this.request<{ user: User; team: APITeam; cli_key: CliClient }>(
        'GET',
        '/internal/console/session',
        params,
      ),
    createTestUrl: (params?: { name?: string; type?: string }) =>
      this.request<{
        // ingest_url (`/e/<id>`) is the send target; inspect_url (`/<id>`) opens
        // the Console. They are not interchangeable — see server console route.
        ingest_url: string;
        inspect_url: string;
        source_id: string;
      }>('POST', '/internal/console/sources/new', params),
  };

  status = {
    getServiceStatus: () => this.request<{ status: 'ok' | 'degraded' }>('GET', `/internal/status`),
  };

  track = {
    event: (event: string, data?: object) =>
      this.request<{ success: boolean }>('POST', '/internal/t', { event, data }).catch(() => false),
  };

  utils = {
    testFilters: (
      request: {
        headers?: object;
        body?: any;
        path?: string;
        parsed_query?: object;
      },
      schema: {
        headers?: WebhookFilterProperty;
        body?: WebhookFilterProperty;
        path?: WebhookFilterProperty;
        query?: WebhookFilterProperty;
      },
    ) =>
      this.request<[boolean, { headers: boolean; body: boolean; path: boolean; query: boolean }]>(
        'PUT',
        `/internal/test_filters`,
        { request, schema },
      ),
  };

  organizations = {
    listAll: () => this.request<APIOrganizationWithRoleAndPlan[]>('GET', `/organizations`),
    create: (data: { name: string; type: ProjectTypeInput; project_name: string }) =>
      this.request<{ organization: APIOrganization; default_team: APITeam }>(
        'POST',
        `/organizations`,
        data,
      ),
    update: (data: OrganizationUpdateInput) =>
      this.request<APIOrganization>('PUT', `/organizations/current`, data),
    delete: () => this.request<{ id: string }>('DELETE', `/organizations/current`),
    leave: () => this.request<{ id: string }>('DELETE', `/organizations/current/leave`),
    getSubscription: () =>
      this.request<APISubscription>('GET', `/organizations/current/subscription`),
    getNextInvoice: () =>
      this.request<{
        currency: string;
        period_end: number;
        period_start: number;
        total: number;
      }>('GET', `/organizations/current/subscription/next_invoice`),
    listMembers: () => this.request<OrganizationMember[]>('GET', `/organizations/current/members`),
    listMemberInvites: () =>
      this.request<OrganizationMemberInvite[]>('GET', `/organizations/current/invites`),
    resendInvite: (invite_id: string) =>
      this.request<OrganizationMemberInvite>(
        'POST',
        `/organizations/current/invites/${invite_id}/resend`,
      ),
    revokeInvite: (invite_id: string) =>
      this.request<{ id: string }>('DELETE', `/organizations/current/invites/${invite_id}`),
    addMembers: (members: { email: string; role: OrganizationRole }[]) =>
      this.request<{
        members: OrganizationMember[];
        invites: OrganizationMemberInvite[];
        subscription: APISubscription;
      }>('POST', `/organizations/current/members`, { members }),
    updateRole: (member_id: string, role: OrganizationRole) =>
      this.request<{ member: OrganizationMember }>(
        'PUT',
        `/organizations/current/members/${member_id}/role`,
        {
          role,
        },
      ),
    removeMember: (member_id: string) =>
      this.request<{ id: string }>('DELETE', `/organizations/current/members/${member_id}`),
    validateInviteToken: (id: string) =>
      this.request<{ organization_name: string }>('GET', `/internal/validate_invite_token/${id}`),
    getSecurity: () =>
      this.request<OrganizationSecuritySettings>('GET', `/organizations/current/security`),
    updateSecurity: (data: OrganizationSecuritySettingsUpdateInput) =>
      this.request<OrganizationSecuritySettings>('PUT', `/organizations/current/security`, data),
    listApiKeys: () => this.request<ApiKey[]>('GET', `/organizations/current/api-keys`),
    createApiKey: (data: {
      label: string;
      type: 'organization' | 'project';
      team_id?: string;
      scopes?: string[];
      // Organization keys: granted project ids; scopes always travel at the root.
      grants?: Record<string, true>;
    }) => this.request<ApiKey>('POST', `/organizations/current/api-keys`, data),
    updateApiKey: (id: string, data: { scopes?: string[]; grants?: Record<string, true> }) =>
      this.request<ApiKey>('PUT', `/organizations/current/api-keys/${id}`, data),
    rollApiKey: (id: string, data: { delay_sec: number }) =>
      this.request<ApiKey>('POST', `/organizations/current/api-keys/${id}/roll`, data),
    deleteApiKey: (id: string) =>
      this.request<{ id: string }>('DELETE', `/organizations/current/api-keys/${id}`),
  };

  teams = {
    listAll: () => this.request<APITeamWithRole[]>('GET', `/projects`),
    create: (data: {
      name: string;
      organization_id: string;
      private: boolean;
      type?: ProjectTypeInput;
    }) => this.request<APITeam>('POST', `/projects`, data),
    update: (data: TeamUpdateInput) => this.request<APITeam>('PUT', `/projects/current`, data),
    delete: () => this.request<{ id: string }>('DELETE', `/projects/current`),
    transfer: (target_organization_id: string) =>
      this.request<{ id: string }>('POST', `/projects/current/transfer`, {
        target_organization_id,
      }),
    leave: () => this.request<{ id: string }>('DELETE', `/projects/current/leave`),
    listMembers: () => this.request<TeamMember[]>('GET', `/projects/current/members`),
    addMembers: (members: { user_id: string; role: TeamRole }[]) =>
      this.request<TeamMember[]>('POST', `/projects/current/members`, { members }),
    updateRole: (member_id: string, role: TeamRole) =>
      this.request<{ member: TeamMember }>('PUT', `/projects/current/members/${member_id}/role`, {
        role,
      }),
    removeMember: (member_id: string) =>
      this.request<{ id: string }>('DELETE', `/projects/current/members/${member_id}`),
    createCustomDomain: (hostname: string) =>
      this.request<CustomDomainAddDeleteResult>('POST', `/projects/current/custom_domains`, {
        hostname,
      }),
    listCustomDomains: () =>
      this.request<CFCustomDomain[]>('GET', `/projects/current/custom_domains`),
    deleteCustomDomain: (domain_id: string) =>
      this.request<CustomDomainAddDeleteResult>(
        'DELETE',
        `/projects/current/custom_domains/${domain_id}`,
      ),
    listApiKeys: () => this.request<ApiKey[]>('GET', `/projects/current/api-keys`),
    createApiKey: (data: { label: string; scopes?: string[] }) =>
      this.request<ApiKey>('POST', `/projects/current/api-keys`, data),
    updateApiKey: (id: string, data: { scopes?: string[] }) =>
      this.request<ApiKey>('PUT', `/projects/current/api-keys/${id}`, data),
    rollApiKey: (id: string, data: { delay_sec: number }) =>
      this.request<ApiKey>('POST', `/projects/current/api-keys/${id}/roll`, data),
    deleteApiKey: (id: string) =>
      this.request<{ id: string }>('DELETE', `/projects/current/api-keys/${id}`),
  };

  issues = {
    get: (id: string) => this.request<IssueWithData>('GET', `/issues/${id}`),
    list: (filters = {}) => this.request<APIList<Issue>>('GET', '/issues', filters),
    count: (filters = {}) => this.request<{ count: number }>('GET', '/issues/count', filters),
    update: (id: string, data) => this.request<Issue>('PUT', `/issues/${id}`, data),
    dismiss: (id: string) => this.request<Issue>('DELETE', `/issues/${id}`),
  };

  issue_triggers = {
    get: (id: string) => this.request<APIIssueTrigger>('GET', `/issue-triggers/${id}`),
    list: (filters = {}) =>
      this.request<APIList<APIIssueTrigger>>('GET', '/issue-triggers', filters),
    count: (filters = {}) =>
      this.request<{ count: number }>('GET', '/issue-triggers/count', filters),
    create: (data) => this.request<APIIssueTrigger>('POST', `/issue-triggers`, data),
    update: (id: string, data) =>
      this.request<APIIssueTrigger>('PUT', `/issue-triggers/${id}`, data),
    delete: (id: string) => this.request<{ id: string }>('DELETE', `/issue-triggers/${id}`),
    disable: (id: string) => this.request<APIIssueTrigger>('PUT', `/issue-triggers/${id}/disable`),
    enable: (id: string) => this.request<APIIssueTrigger>('PUT', `/issue-triggers/${id}/enable`),
    nameIsUsed: (name: string): Promise<boolean> =>
      this.requestWithCache<APIList<APIIssueTrigger>>('GET', `/issue-triggers`, {
        name,
        limit: 1,
      }).then((data) => !!data?.models?.[0]),
    attachChannelsToAll: (data: APIIssueTriggerChannels): Promise<APIIssueTrigger[]> =>
      this.request<APIIssueTrigger[]>('PUT', `/internal/issue-triggers/channels`, data),
  };

  webhooks = {
    get: (id: string) => this.request<APIWebhook>('GET', `/webhooks/${id}`),
    test: (id: string) => this.request<APIWebhook>('POST', `/webhooks/${id}/test`),
    create: (data: object) => this.request<APIWebhook>('POST', `/webhooks`, data),
    upsert: (data: object) => this.request<APIWebhook>('PUT', `/webhooks`, data),
    update: (id: string, data) => this.request<APIWebhook>('PUT', `/webhooks/${id}`, data),
    list: (filters = {}) => this.request<APIList<APIWebhook>>('GET', '/webhooks', filters),
    count: (filters = {}) => this.request<{ count: number }>('GET', '/webhooks/count', filters),
    disable: (id: string) => this.request<APIWebhook>('PUT', `/webhooks/${id}/disable`),
    enable: (id: string) => this.request<APIWebhook>('PUT', `/webhooks/${id}/enable`),
    pause: (id: string) => this.request<APIWebhook>('PUT', `/webhooks/${id}/pause`),
    unpause: (id: string) => this.request<APIWebhook>('PUT', `/webhooks/${id}/unpause`),
    delete: (id: string) => this.request<Pick<APIWebhook, 'id'>>('DELETE', `/webhooks/${id}`),
    nameIsUsed: (source_id: string, name: string): Promise<boolean> =>
      this.requestWithCache<APIList<APIWebhook>>('GET', `/webhooks`, {
        name,
        source_id,
        limit: 1,
      }).then((data) => !!data?.models?.[0]),
  };

  destinations = {
    get: (id: string, filters = {}) =>
      this.request<APIDestination>('GET', `/destinations/${id}`, filters),
    delete: (id: string) =>
      this.request<Pick<APIDestination, 'id'>>('DELETE', `/destinations/${id}`),
    create: (data: object) => this.request<APIDestination>('POST', `/destinations`, data),
    update: (id: string, data) => this.request<APIDestination>('PUT', `/destinations/${id}`, data),
    disable: (id: string) => this.request<APIDestination>('PUT', `/destinations/${id}/disable`),
    enable: (id: string) => this.request<APIDestination>('PUT', `/destinations/${id}/enable`),
    list: (filters = {}) => this.request<APIList<APIDestination>>('GET', '/destinations', filters),
    count: (filters = {}) => this.request<{ count: number }>('GET', '/destinations/count', filters),
    nameIsUsed: (name: string): Promise<boolean> =>
      this.requestWithCache<APIList<APIDestination>>('GET', `/destinations`, {
        name,
        limit: 1,
      }).then((data) => !!data?.models?.[0]),
  } as const;

  destinationTypes = {
    list: () =>
      this.request<{
        [key in DestinationType]: DestinationTypeSchema;
      }>('GET', '/internal/destination-types'),
  };

  sources = {
    get: (id: string, filters = {}) => this.request<APISource>('GET', `/sources/${id}`, filters),
    delete: (id: string) => this.request<Pick<APISource, 'id'>>('DELETE', `/sources/${id}`),
    create: (data: object) => this.request<APISource>('POST', `/sources`, data),
    update: (id: string, data) => this.request<APISource>('PUT', `/sources/${id}`, data),
    disable: (id: string) => this.request<APISource>('PUT', `/sources/${id}/disable`),
    enable: (id: string) => this.request<APISource>('PUT', `/sources/${id}/enable`),
    list: (filters = {}) => this.request<APIList<APISource>>('GET', '/sources', filters),
    count: (filters = {}) => this.request<{ count: number }>('GET', '/sources/count', filters),
    nameIsUsed: (name: string): Promise<boolean> =>
      this.requestWithCache<APIList<APISource>>('GET', `/sources`, {
        name,
        limit: 1,
      }).then((data) => !!data?.models?.[0]),
  };

  sourceTypes = {
    list: () =>
      this.request<{
        [key in SourceType]: SourceTypeSchema;
      }>('GET', '/internal/source-types'),
  };

  bookmarks = {
    get: (id: string) => this.request<Bookmark>('GET', `/bookmarks/${id}`),
    getRawBody: (id: string, file?: { field: string; index: number }) =>
      this.request<RawBodyResponse>(
        'GET',
        `/bookmarks/${id}/raw_body${file ? `?multipart_field=${encodeURIComponent(file.field)}&multipart_index=${file.index}` : ''}`,
      ),
    update: (id: string, data: object) => this.request<Bookmark>('PUT', `/bookmarks/${id}`, data),
    create: (data) => this.request<Bookmark>('POST', `/bookmarks`, data),
    list: (filters = {}) => this.request<APIList<Bookmark>>('GET', '/bookmarks', filters),
    trigger: (id: string) => this.request<Event[]>('POST', `/bookmarks/${id}/trigger`),
    delete: (id: string) => this.request<{ id: string }>('DELETE', `/bookmarks/${id}`),
  };

  team_integrations = {
    create: (data) => this.request<TeamIntegration>('POST', `/session/team-integrations`, data),
    list: (filters = {}) =>
      this.request<APIList<TeamIntegration>>('GET', '/session/team-integrations', filters),
    delete: (id: string) =>
      this.request<{ id: string; provider_cleanup_failed?: boolean }>(
        'DELETE',
        `/session/team-integrations/${id}`,
      ),
    reauthorize: (id: string, data) =>
      this.request<TeamIntegration>('PUT', `/session/team-integrations/${id}/reauthorize`, data),
  };

  transformations = {
    get: (id: string) => this.request<Transformation>('GET', `/transformations/${id}`),
    delete: (id: string) => this.request<{ id: string }>('DELETE', `/transformations/${id}`),
    update: (id: string, data: object) =>
      this.request<Transformation>('PUT', `/transformations/${id}`, data),
    create: (data) => this.request<Transformation>('POST', `/transformations`, data),
    list: (filters = {}) =>
      this.request<APIList<Transformation>>('GET', '/transformations', filters),
    count: (filters = {}) =>
      this.request<{ count: number }>('GET', '/transformations/count', filters),
    listExecutions: (id: string, filters = {}) =>
      this.request<APIList<TransformationExecution>>(
        'GET',
        `/transformations/${id}/executions`,
        filters,
      ),
    getExecution: (id: string, execution_id: string) =>
      this.request<TransformationExecution>(
        'GET',
        `/transformations/${id}/executions/${execution_id}`,
      ),
    run: (props: TransformationRunInput) =>
      this.request<TransformationExecutorOutput>('PUT', `/transformations/run`, props),
    nameIsUsed: (name: string): Promise<boolean> =>
      this.requestWithCache<APIList<Transformation>>('GET', `/transformations`, {
        name,
        limit: 1,
      }).then((data) => !!data?.models?.[0]),
    hasConnections: (id: string) =>
      this.request<{ has_connections: boolean }>(
        'GET',
        `/internal/transformations/${id}/has-connections`,
      ),
    getConnections: (id: string) =>
      this.request<{ count: number; models: APIWebhook[] }>(
        'GET',
        `/internal/transformations/${id}/connections`,
      ),
  };

  requests = {
    // `webhook_id` shapes a multipart body the way that connection's
    // transformation receives it (transformation editor samples).
    get: (id: string, params: { webhook_id?: string } = {}) =>
      this.request<Request>('GET', `/requests/${id}`, params),
    retry: (id: string, webhook_ids: string[] = []) =>
      this.request<{ request: Request; events: Event[] }>('POST', `/requests/${id}/retry`, {
        webhook_ids,
      }),
    replay: (id: string, target?: { source_id?: string; webhook_ids?: string[] }) =>
      this.request<{ requests: Request[]; events: Event[] }>(
        'POST',
        `/requests/${id}/replay`,
        target ?? {},
      ),
    getRawBody: (id: string, file?: { field: string; index: number }) =>
      this.request<RawBodyResponse>(
        'GET',
        `/requests/${id}/raw_body${file ? `?multipart_field=${encodeURIComponent(file.field)}&multipart_index=${file.index}` : ''}`,
      ),
    list: (filters = {}, signal?: AbortSignal) =>
      this.request<APIList<Request>>('GET', '/requests', filters, signal),
    listEvents: (id: string, filters = {}) =>
      this.request<APIList<Event>>('GET', `/requests/${id}/events`, filters),
    listIgnoredEvents: (id: string, filters = {}) =>
      this.request<APIList<IgnoredEvent>>('GET', `/requests/${id}/ignored_events`, filters),
  };

  events = {
    get: (id: string) => this.request<EventWithData>('GET', `/events/${id}`),
    getRawBody: (id: string, file?: { field: string; index: number }) =>
      this.request<RawBodyResponse>(
        'GET',
        `/events/${id}/raw_body${file ? `?multipart_field=${encodeURIComponent(file.field)}&multipart_index=${file.index}` : ''}`,
      ),
    list: (filters = {}, signal?: AbortSignal) =>
      this.request<APIList<EventWithData>>('GET', '/events', filters, signal),
    retry: (id: string) => this.request<Event>('POST', `/events/${id}/retry`),
    replay: (id: string, webhook_ids?: string[]) =>
      this.request<{ requests: Request[]; events: Event[] }>(
        'POST',
        `/events/${id}/replay`,
        webhook_ids ? { webhook_ids } : {},
      ),
    mute: (id: string) => this.request<Event>('PUT', `/events/${id}/mute`),
    cancel: (id: string) => this.request<Event>('PUT', `/events/${id}/cancel`),
    testFilters: (
      id: string,
      filters: {
        headers?: WebhookFilterProperty;
        body?: WebhookFilterProperty;
        path?: WebhookFilterProperty;
        query?: WebhookFilterProperty;
      },
    ) =>
      this.request<[boolean, { headers: boolean; body: boolean; path: boolean; query: boolean }]>(
        'PUT',
        `/events/${id}/filter`,
        filters,
      ),
  };

  ignored_events = {
    list: (filters = {}, signal?: AbortSignal) =>
      this.request<APIList<IgnoredEvent>>('GET', '/ignored-events', filters, signal),
  };

  attempts = {
    get: (id: string) => this.request<EventAttempt>('GET', `/attempts/${id}`),
    list: (filters = {}) => this.request<APIList<EventAttempt>>('GET', '/attempts', filters),
  };

  bulk = {
    events: {
      get: (id: string) => this.request<APIBatchOperation>('GET', `/bulk/events/retry/${id}`),
      list: (filters = {}) =>
        this.request<APIList<APIBatchOperation>>('GET', '/bulk/events/retry', filters),
      count: (filters = {}) =>
        this.request<{ count: number }>('GET', '/bulk/events/retry/count', filters),
      plan: (filters) =>
        this.request<BatchOperationPlan>('GET', `/bulk/events/retry/plan`, filters),
      retry: (filters) =>
        this.request<APIBatchOperation>('POST', `/bulk/events/retry`, {
          ...filters,
        }),
      cancel: (id: string) =>
        this.request<APIBatchOperation>('POST', `/bulk/events/retry/${id}/cancel`),
    },
    cancelEvents: {
      get: (id: string) => this.request<APIBatchOperation>('GET', `/bulk/events/cancel/${id}`),
      list: (filters = {}) =>
        this.request<APIList<APIBatchOperation>>('GET', '/bulk/events/cancel', filters),
      plan: (filters) =>
        this.request<BatchOperationPlan>('GET', `/bulk/events/cancel/plan`, filters),
      create: (filters) =>
        this.request<APIBatchOperation>('POST', `/bulk/events/cancel`, {
          ...filters,
        }),
    },
    requests: {
      get: (id: string) => this.request<APIBatchOperation>('GET', `/bulk/requests/retry/${id}`),
      list: (filters = {}) =>
        this.request<APIList<APIBatchOperation>>('GET', '/bulk/requests/retry', filters),
      count: (filters = {}) =>
        this.request<{ count: number }>('GET', '/bulk/requests/retry/count', filters),
      plan: (filters) =>
        this.request<BatchOperationPlan>('GET', `/bulk/requests/retry/plan`, filters),
      retry: (filters) =>
        this.request<APIBatchOperation>('POST', `/bulk/requests/retry`, {
          ...filters,
        }),
      cancel: (id: string) =>
        this.request<APIBatchOperation>('POST', `/bulk/requests/retry/${id}/cancel`),
    },
    requestsReplay: {
      get: (id: string) => this.request<APIBatchOperation>('GET', `/bulk/requests/replay/${id}`),
      list: (filters = {}) =>
        this.request<APIList<APIBatchOperation>>('GET', '/bulk/requests/replay', filters),
      count: (filters = {}) =>
        this.request<{ count: number }>('GET', '/bulk/requests/replay/count', filters),
      plan: (filters) =>
        this.request<BatchOperationPlan>('GET', `/bulk/requests/replay/plan`, filters),
      create: (filters) =>
        this.request<APIBatchOperation>('POST', `/bulk/requests/replay`, {
          ...filters,
        }),
      cancel: (id: string) =>
        this.request<APIBatchOperation>('POST', `/bulk/requests/replay/${id}/cancel`),
    },
    ignored_events: {
      get: (id: string) =>
        this.request<APIBatchOperation>('GET', `/bulk/ignored-events/retry/${id}`),
      list: (filters = {}) =>
        this.request<APIList<APIBatchOperation>>('GET', '/bulk/ignored-events/retry', filters),
      count: (filters = {}) =>
        this.request<{ count: number }>('GET', '/bulk/ignored-events/retry/count', filters),
      plan: (filters) =>
        this.request<BatchOperationPlan>('GET', `/bulk/ignored-events/retry/plan`, filters),
      retry: (filters) =>
        this.request<APIBatchOperation>('POST', `/bulk/ignored-events/retry`, {
          ...filters,
        }),
      cancel: (id: string) =>
        this.request<APIBatchOperation>('POST', `/bulk/ignored-events/retry/${id}/cancel`),
    },
  };

  cli_clients = {
    list: (filters?: object) => this.request<APIList<CliClient>>('GET', '/cli', filters),
    getSessions: (filters?: object) => this.request<CliSession[]>('GET', '/cli-sessions', filters),
    create: (body?: { device_name?: string | null }) =>
      this.request<CliClient>('POST', '/cli', body ?? {}),
  };

  auth = {
    signin: (
      { email, password }: { email: string; password: string },
      query,
    ): Promise<LoginResponse> =>
      this.request('POST', `/signin/email`, { ...query, email, password }),
    signup: (
      {
        email,
        password,
        name,
        product_source,
      }: { email: string; password: string; name?: string; product_source: string },
      query,
    ): Promise<LoginResponse> =>
      this.request('POST', `/signup/email`, { ...query, email, password, name, product_source }),
    sendPasswordReset: (email): Promise<{ success: boolean }> =>
      this.request('POST', `/password/forgot`, { email }),
    resetPassword: (password, token): Promise<{ redirect: string }> =>
      this.request('POST', `/password/reset`, { password, token }),
    getSSOUrl: (email?: string, organization_slug?: string) =>
      this.request<{ url: string }>('GET', `/signin/workos/get-auth-url`, {
        email,
        organization_slug,
      }),
    verifyMultiFactor: (
      token: string,
      code: string,
      redirect?: string,
      invite_token?: string,
    ): Promise<LoginSuccessResponse> =>
      this.request('POST', `/auth/mfa/verify`, {
        token,
        code,
        redirect,
        invite_token,
      }),
  };

  multi_factor = {
    /**
     * Get MFA status for current user
     */
    getStatus: () => this.request<MultiFactorStatusResponse>('GET', '/mfa/status'),

    /**
     * List all verified authenticators for current user
     */
    listAuthenticators: () => this.request<Authenticator[]>('GET', '/mfa/authenticators'),

    /**
     * Start adding a new authenticator
     * Returns TOTP URI and secret for QR code generation
     * Note: Name is auto-generated on the server (UUID)
     * Requires password confirmation if user has a password set
     */
    addAuthenticator: (password?: string) =>
      this.request<MultiFactorSetupResponse>('POST', '/mfa/authenticators', {
        ...(password && { password }),
      }),

    /**
     * Verify TOTP code and activate authenticator
     * Returns backup codes on first authenticator setup
     */
    verifyAuthenticator: (id: string, code: string) =>
      this.request<MultiFactorVerifyAuthenticatorResponse>(
        'POST',
        `/mfa/authenticators/${id}/verify`,
        { code },
      ),

    /**
     * Delete an authenticator (requires password confirmation)
     */
    deleteAuthenticator: (id: string, password: string) =>
      this.request<{ success: boolean }>('DELETE', `/mfa/authenticators/${id}`, {
        password,
      }),

    /**
     * Generate new backup codes (invalidates existing ones, requires password confirmation)
     */
    generateBackupCodes: (password: string) =>
      this.request<BackupCodesResponse>('POST', '/mfa/backup-codes', { password }),

    /**
     * Get count of remaining unused backup codes
     */
    getBackupCodesCount: () =>
      this.request<BackupCodesCountResponse>('GET', '/mfa/backup-codes/count'),

    /**
     * Disable MFA entirely (requires password confirmation)
     */
    disableMFA: (password: string) =>
      this.request<{ success: boolean }>('POST', '/mfa/disable', { password }),
  };

  session = {
    getCurrentSigningSecret: () => this.request<SigningSecret>('GET', `/session/signing_secret`),
    me: () => this.request<User>('GET', `/session/me`),
    context: (): Promise<{
      team: APITeam;
      team_role: TeamRole;
      organization: APIOrganization;
      organization_role: OrganizationRole;
      subscription: APISubscription;
    }> => this.request('GET', `/session/context`),
    updateMe: (data: object) => this.request<User>('PUT', `/session/me`, data),
    updatePassword: (password: string, old_password: string) =>
      this.request<{ success: true }>('POST', `/session/me/change_password`, {
        password,
        old_password,
      }),

    // Without access, the login reaches everything the user can and never expires.
    claimCliAuthToken: (key, team_id?: string, access?: CliKeyAccessInput): Promise<boolean> =>
      this.request('POST', `/cli-auth/claim`, { key, team_id, ...access }),
    logout: () => this.request<null | { redirect_url?: string }>('POST', '/session/logout'),
    // The keys made on the Keys & Sessions page.
    listCliKeys: (filters?: object) =>
      this.request<APIList<APIAccountCliKey>>('GET', '/session/me/cli_keys', filters),
    revokeCliKey: (id: string) =>
      this.request<{ id: string }>('DELETE', `/session/me/cli_keys/${id}`),
    // Everywhere the user is logged in: browsers and `hookdeck login`.
    listSessions: () => this.request<APIAccountSessionList>('GET', '/session/me/sessions'),
    revokeSession: (id: string) =>
      this.request<{ id: string }>('DELETE', `/session/me/sessions/${id}`),
    revokeAllSessions: () => this.request<{ count: number }>('DELETE', '/session/me/sessions'),
    createCliToken: (data: { name: string } & CliKeyAccessInput) =>
      this.request<APICreatedCliToken>('POST', '/session/me/cli_keys', data),
    rollSigningSecret: (id: string, data: { delay_sec: number }) =>
      this.request<SigningSecret>('POST', `/session/signing_secrets/${id}/roll`, data),

    /**
     * Get MFA compliance status for the current user against a specific organization.
     */
    getMFACompliance: (organization_id: string) =>
      this.request<MFAComplianceStatus>('GET', `/session/mfa-compliance`, { organization_id }),
  };

  stats = {
    token: (): Promise<{ token: string }> => this.request('GET', `/stats/token`),
  };

  views = {
    list: <SpecificView = View>(filters = {}) =>
      this.request<APIList<SpecificView>>('GET', '/views', filters),
    create: (data: ViewCreateInput) => this.request<View>('POST', `/views`, data),
    update: (id: string, data: ViewUpdateInput) => this.request<View>('PUT', `/views/${id}`, data),
    delete: (id: string) => this.request<{ id: string }>('DELETE', `/views/${id}`),
  };

  outpost = {
    getConfig: () => this.outpostRequest<OperatorConfig>('GET', '/config'),
    updateConfig: (config: OperatorConfigUpdateInput) =>
      this.outpostRequest<OperatorConfig>('PATCH', '/config', config),
    getStatus: () => this.outpostRequest<OutpostStatusResponse>('GET', '/status'),
    getCustomDomain: () =>
      this.outpostRequest<CFCustomDomain | null>('GET', '/config/custom_domain'),
    addCustomDomain: (hostname: string) =>
      this.outpostRequest<CFCustomDomain>('POST', '/config/custom_domain', { hostname }),
    deleteCustomDomain: () =>
      this.outpostRequest<{ success: boolean }>('DELETE', '/config/custom_domain'),
    tenants: {
      list: (filters?: any) =>
        this.outpostRequest<APIList<OutpostTenant>>('GET', '/tenants', filters),
      get: (tenant_id: string) =>
        this.outpostRequest<OutpostTenant>('GET', `/tenants/${tenant_id}`),
      tenantIdIsUsed: (tenant_id: string): Promise<boolean> =>
        this.outpostRequest<OutpostTenant>(
          'GET',
          `/tenants/${tenant_id}`,
          undefined,
          undefined,
          [404],
        ).then((tenant) => tenant !== null),
      update: (tenant_id: string, data: OutpostTenantUpsertInput) =>
        this.outpostRequest<OutpostTenant>('PUT', `/tenants/${tenant_id}`, data),
      delete: (tenant_id: string) =>
        this.outpostRequest<{ id: string }>('DELETE', `/tenants/${tenant_id}`),
      getToken: (tenant_id: string) =>
        this.outpostRequest<OutpostTenantTokenResponse>('GET', `/tenants/${tenant_id}/token`),
      getPortal: (tenant_id: string) =>
        this.outpostRequest<OutpostTenantPortalResponse>('GET', `/tenants/${tenant_id}/portal`),
    },
    destinationTypes: {
      list: () =>
        this.outpostRequest<OutpostDestinationTypeMetadata[]>('GET', `/destination-types`),
      get: (type: string) =>
        this.outpostRequest<OutpostDestinationTypeMetadata>('GET', `/destination-types/${type}`),
    },
    destinations: {
      list: (tenant_id: string, filters?: OutpostDestinationListParams) =>
        this.outpostRequest<OutpostDestination[]>(
          'GET',
          `/tenants/${tenant_id}/destinations`,
          filters,
        ),
      get: (tenant_id: string, destination_id: string) =>
        this.outpostRequest<OutpostDestination>(
          'GET',
          `/tenants/${tenant_id}/destinations/${destination_id}`,
        ),
      create: (tenant_id: string, data: OutpostDestinationCreateInput) =>
        this.outpostRequest<OutpostDestination>('POST', `/tenants/${tenant_id}/destinations`, data),
      update: (tenant_id: string, destination_id: string, data: OutpostDestinationUpdateInput) =>
        this.outpostRequest<OutpostDestination>(
          'PATCH',
          `/tenants/${tenant_id}/destinations/${destination_id}`,
          data,
        ),
      delete: (tenant_id: string, destination_id: string) =>
        this.outpostRequest<{ id: string }>(
          'DELETE',
          `/tenants/${tenant_id}/destinations/${destination_id}`,
        ),
      enable: (tenant_id: string, destination_id: string) =>
        this.outpostRequest<OutpostDestination>(
          'PUT',
          `/tenants/${tenant_id}/destinations/${destination_id}/enable`,
        ),
      disable: (tenant_id: string, destination_id: string) =>
        this.outpostRequest<OutpostDestination>(
          'PUT',
          `/tenants/${tenant_id}/destinations/${destination_id}/disable`,
        ),
    },
    topics: {
      list: (tenant_id: string) =>
        this.outpostRequest<string[]>('GET', `/tenants/${tenant_id}/topics`),
    },
    events: {
      list: (filters?: OutpostEventListParams) =>
        this.outpostRequest<OutpostEventListResponse>('GET', '/events', filters),
      get: (event_id: string) => this.outpostRequest<OutpostEvent>('GET', `/events/${event_id}`),
    },
    attempts: {
      list: (filters?: OutpostAttemptListParams) =>
        this.outpostRequest<APIList<OutpostAttempt>>('GET', '/attempts', filters),
      get: (attempt_id: string, filters?: Pick<OutpostAttemptListParams, 'include'>) =>
        this.outpostRequest<OutpostAttempt>('GET', `/attempts/${attempt_id}`, filters),
    },
    retries: {
      retry: (event_id: string, destination_id: string) =>
        this.outpostRequest<OutpostRetryResponse>('POST', `/retry`, {
          event_id,
          destination_id,
        }),
    },
    publish: {
      create: (data: OutpostPublishEventInput) =>
        this.outpostRequest<OutpostEvent>('POST', '/publish', data),
    },
    operatorEvents: {
      destinationTypes: {
        list: () =>
          this.outpostRequest<OutpostDestinationTypeMetadata[]>(
            'GET',
            '/operator-events/destination-types',
          ),
      },
      destinations: {
        list: () =>
          this.outpostRequest<OutpostDestination[]>('GET', '/operator-events/destinations'),
        get: (destination_id: string) =>
          this.outpostRequest<OutpostDestination>(
            'GET',
            `/operator-events/destinations/${destination_id}`,
          ),
        create: (data: OutpostDestinationCreateInput) =>
          this.outpostRequest<OutpostDestination>('POST', '/operator-events/destinations', data),
        update: (destination_id: string, data: OutpostDestinationUpdateInput) =>
          this.outpostRequest<OutpostDestination>(
            'PATCH',
            `/operator-events/destinations/${destination_id}`,
            data,
          ),
        delete: (destination_id: string) =>
          this.outpostRequest<{ id: string }>(
            'DELETE',
            `/operator-events/destinations/${destination_id}`,
          ),
        enable: (destination_id: string) =>
          this.outpostRequest<OutpostDestination>(
            'PUT',
            `/operator-events/destinations/${destination_id}/enable`,
          ),
        disable: (destination_id: string) =>
          this.outpostRequest<OutpostDestination>(
            'PUT',
            `/operator-events/destinations/${destination_id}/disable`,
          ),
      },
      events: {
        list: (filters?: OutpostEventListParams) =>
          this.outpostRequest<OutpostEventListResponse>('GET', '/operator-events/events', filters),
        get: (event_id: string) =>
          this.outpostRequest<OutpostEvent>('GET', `/operator-events/events/${event_id}`),
      },
      attempts: {
        get: (attempt_id: string, options?: { include?: string[] }) =>
          this.outpostRequest<OutpostAttempt>(
            'GET',
            `/operator-events/attempts/${attempt_id}`,
            options,
          ),
        list: (filters?: OutpostAttemptListParams) =>
          this.outpostRequest<APIList<OutpostAttempt>>('GET', '/operator-events/attempts', filters),
      },
      retries: {
        retry: (event_id: string, destination_id: string) =>
          this.outpostRequest<OutpostRetryResponse>('POST', `/operator-events/retry`, {
            event_id,
            destination_id,
          }),
      },
    },
  };
}
